/**
 * Maps legacy MUL hash URLs to HTML5 router paths for the universal-login shell.
 *
 * Legacy AngularJS routes look like `/u/login/?gta=administration#/login` or
 * `#/request-cloud-password-reset/`. Secure-messaging bridges keep hash query
 * params (`#/login?message=...`). This app serves `/u/login/...` without a hash.
 *
 * Only hashes whose first path segment matches a known MUL login route are
 * redirected. Unknown hashes (e.g. `#/home` from App Launcher) return null so
 * Angular loads the original URL intact and query params such as gta/link are
 * preserved for the post-login redirect.
 */

// First path-segment values that correspond to real MUL login routes.
// Hashes whose first segment is NOT in this set are passed through unchanged
// (return null). Add new login routes here when they are introduced.
const KNOWN_MUL_HASH_ROUTES = new Set([
  '',
  'login',
  'otp',
  '2-step',
  '2-step-setup',
  'mfa', // legacy hash route from old MUL; no current login.routes.ts entry but kept for backward compat
  'request-cloud-password-reset',
  'reset-password',
  'forgot-password',
  'expired-password',
  'otp-registration',
  'change-password',
]);

export function buildLegacyHashRedirectUrl(
  hash: string,
  search: string
): string | null {
  if (!hash.startsWith('#/')) {
    return null;
  }

  // Strip "#/" then split route from hash-local query (`#/login?message=...`).
  // Treating `login?message=...` as the route name would navigate to
  // `/login/login?message=...?gta=...` and glue search params into `message`.
  const hashWithoutPrefix = hash.slice(2);
  const hashQueryIndex = hashWithoutPrefix.indexOf('?');
  const hashPath =
    hashQueryIndex === -1
      ? hashWithoutPrefix
      : hashWithoutPrefix.slice(0, hashQueryIndex);
  const hashSearch =
    hashQueryIndex === -1 ? '' : hashWithoutPrefix.slice(hashQueryIndex);

  // Strip &-delimited inline params from the hash path before extracting the
  // route name. Old AngularJS apps embed return-URL params directly in the hash
  // path (e.g. #/login&r=/return-url or #/login&p=base64path). UI-Router's
  // otherwise('/login') catch-all silently dropped these; new MUL does the
  // same by discarding them here. The post-login destination is already carried
  // by the standard query params (link=, gta=).
  // The trailing replace strips any "/" suffix, e.g. "request-cloud-password-reset/" -> "request-cloud-password-reset".
  const hashRoute = hashPath.split('&')[0].replace(/\/$/, '');

  // Guard against non-MUL hashes (e.g. App Launcher `#/home`, AdCon `#/applications`).
  // Old MUL silently fell through to `$urlRouterProvider.otherwise('/login')` for
  // unknown routes, which stripped query params. Returning null here keeps the
  // original URL intact so Angular processes it directly and gta/link survive.
  const firstSegment = hashRoute.split('/')[0];
  if (!KNOWN_MUL_HASH_ROUTES.has(firstSegment)) {
    return null;
  }

  // "#/login" is the login entry itself, not a child - map to /login directly.
  // Every other hash route is a child under /login/.
  const targetPath =
    hashRoute === 'login' || hashRoute === ''
      ? '/login'
      : `/login/${hashRoute}`;

  // Preserve query params from the landing URL (e.g. ?gta=administration).
  // Strip any trailing slash from the search string: URLs constructed as
  // "/u/login/?gta=admin/#/login" cause the browser to include the "/"
  // separator in window.location.search ("?gta=admin/"), which would
  // otherwise be forwarded as a literal %2F in the query value.
  const normalizedSearch = search.replace(/\/$/, '');

  return targetPath + mergeQueryStrings(normalizedSearch, hashSearch);
}

/**
 * Hash-local query from `#/login?message=...` (empty when the hash has no `?`).
 * Used to fold bridge params into `ConfigService.initialUrlParams` at bootstrap.
 */
export function extractHashSearchParams(hash: string): URLSearchParams {
  if (!hash.startsWith('#/')) {
    return new URLSearchParams();
  }

  const hashWithoutPrefix = hash.slice(2);
  const hashQueryIndex = hashWithoutPrefix.indexOf('?');
  if (hashQueryIndex === -1) {
    return new URLSearchParams();
  }

  return new URLSearchParams(hashWithoutPrefix.slice(hashQueryIndex));
}

function mergeQueryStrings(search: string, hashSearch: string): string {
  const parts = [search, hashSearch]
    .map((value) => value.replace(/^\?/, '').replace(/\/$/, ''))
    .filter(Boolean);

  return parts.length ? `?${parts.join('&')}` : '';
}
